import React, { useState, useContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Panel } from "react-instantsearch-dom";
import { cn } from "utils";
import { FilterPanelContext } from "context/providers/FilterPanelProvider";

const CollapsiblePanel = ({
  header,
  sectionKey,
  defaultCollapsed = true,
  children,
  className,
  collapseTrigger,
  chevronPosition = "left",
  headerClassName,
  overlay = false,
  overlayClassName,
  headerSuffix,
  excludeFromExpandAll = false,
  ...panelProps
}) => {
  const storageKey = useMemo(
    () => `filter-section-${sectionKey}`,
    [sectionKey]
  );

  const getInitialCollapsedState = () => {
    if (!defaultCollapsed) {
      return false;
    }

    const storedState = sessionStorage.getItem(storageKey);
    if (storedState !== null) {
      return storedState === "true";
    }

    return defaultCollapsed;
  };

  const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsedState());
  const context = useContext(FilterPanelContext);

  useEffect(() => {
    if (!context || excludeFromExpandAll) return;

    const updateCallback = (collapsed) => {
      setIsCollapsed(collapsed);
      sessionStorage.setItem(storageKey, collapsed.toString());
    };

    context.registerPanel(sectionKey, updateCallback);
    return () => context.unregisterPanel(sectionKey);
  }, [sectionKey, context, storageKey, excludeFromExpandAll]);

  useEffect(() => {
    if (!collapseTrigger) {
      return;
    }

    setIsCollapsed(true);
    sessionStorage.setItem(storageKey, "true");
  }, [collapseTrigger, storageKey]);

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    sessionStorage.setItem(storageKey, newState.toString());
  };

  const chevronIcon = (
    <svg
      className={cn(
        "h-3 w-3 shrink-0 fill-gray-400 transition-transform flex items-center justify-center",
        {
          "fill-gray-600": !isCollapsed,
          "-rotate-90": chevronPosition === "left" && isCollapsed,
          "rotate-180": chevronPosition === "right" && !isCollapsed,
        }
      )}
      viewBox="0 0 9 5"
    >
      <path
        d="M4.5 5.55L0 1.05L1.05 0L4.5 3.45L7.95 0L9 1.05L4.5 5.55Z"
        fill="currentColor"
      />
    </svg>
  );

  const headerSuffixNode = headerSuffix ? (
    <span
      className="ml-auto shrink-0 pl-2"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {headerSuffix}
    </span>
  ) : null;

  const headerContent =
    chevronPosition === "right" ? (
      <div
        className="flex w-full cursor-pointer select-none items-center justify-between gap-2"
        onClick={toggleCollapse}
      >
        <span className={cn("min-w-0 truncate", headerClassName)}>
          {header}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          {headerSuffixNode}
          {chevronIcon}
        </div>
      </div>
    ) : (
      <div
        className="flex w-full cursor-pointer select-none items-center"
        onClick={toggleCollapse}
      >
        <span className="mr-3">{chevronIcon}</span>
        <span className={cn("min-w-0", headerClassName)}>{header}</span>
        {headerSuffixNode}
      </div>
    );

  return (
    <div
      className={cn("font-rubik", className, { relative: overlay })}
      data-collapsed={isCollapsed}
    >
      <Panel header={headerContent} {...panelProps}>
        <div
          className={cn(
            {
              "h-0 overflow-hidden": isCollapsed && !overlay,
              "h-auto": !isCollapsed && !overlay,
              hidden: isCollapsed && overlay,
            },
            overlay &&
              !isCollapsed &&
              cn(
                "absolute left-0 right-0 top-full z-20 mt-1 rounded-md border border-slate-300 bg-white py-2 pl-[18px] pr-6 shadow-md",
                overlayClassName
              )
          )}
        >
          {children}
        </div>
      </Panel>
    </div>
  );
};

CollapsiblePanel.propTypes = {
  header: PropTypes.string.isRequired,
  sectionKey: PropTypes.string.isRequired,
  defaultCollapsed: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  collapseTrigger: PropTypes.number,
  chevronPosition: PropTypes.oneOf(["left", "right"]),
  headerClassName: PropTypes.string,
  overlay: PropTypes.bool,
  overlayClassName: PropTypes.string,
  headerSuffix: PropTypes.node,
  excludeFromExpandAll: PropTypes.bool,
};

export default CollapsiblePanel;
